import type { AuthoredStaticCollisionSpec } from "../physics/index.ts";

export type TraversalAccessKey = "blueGate" | "rooftopShortcut";

export interface TraversalDiscoverySite {
  readonly id: string;
  readonly label: string;
  readonly connection: string;
  readonly position: Readonly<{ x: number; z: number }>;
  readonly radiusM: number;
}

export interface TraversalGate {
  readonly id: string;
  readonly label: string;
  readonly position: Readonly<{ x: number; y: number; z: number }>;
  readonly widthM: number;
  readonly heightM: number;
  readonly depthM: number;
  readonly rotationYRadians: number;
  readonly access: TraversalAccessKey;
  readonly lockedMessage: string;
}

export const VILNIUS_TRAVERSAL_MANIFEST_URL =
  "/assets/traversal/manifest.json";

/**
 * Reviewed, street-level approaches used by City Lab to inspect the route
 * network without weakening its in-world discovery or access rules.
 */
export const TRAVERSAL_REVIEW_DESTINATIONS = Object.freeze([
  Object.freeze({
    id: "traversal-blue-courtyard-gate",
    label: "Route: Blue courtyard gate",
    tileId: "1:1",
    arrivalPosition: Object.freeze({ x: 504, z: 379 }),
    facingYaw: Math.atan2(506.272 - 504, 384.538 - 379),
  }),
  Object.freeze({
    id: "traversal-love-arch",
    label: "Route: Love Arch passage",
    tileId: "1:3",
    arrivalPosition: Object.freeze({ x: 374.5, z: 1000.1 }),
    facingYaw: Math.atan2(360 - 374.5, 995.2 - 1000.1),
  }),
  Object.freeze({
    id: "traversal-bekesas-steps",
    label: "Route: Bekešas hill steps",
    tileId: "5:0",
    arrivalPosition: Object.freeze({ x: 1316.2, z: 249.3 }),
    facingYaw: Math.atan2(1.221, -5.088),
  }),
]);

export const TRAVERSAL_DISCOVERY_SITES: readonly Readonly<
  TraversalDiscoverySite
>[] = Object.freeze([
  {
    id: "university-courtyards",
    label: "University courtyard chain",
    connection: "Universiteto g. to the inner university courts",
    position: { x: 569.926, z: 628.771 },
    radiusM: 22,
  },
  {
    id: "pilies-22-courtyard",
    label: "Pilies 22 courtyard",
    connection: "Pilies g. to the former botanical garden court",
    position: { x: 744.422, z: 627.028 },
    radiusM: 18,
  },
  {
    id: "printing-courtyard",
    label: "Printing Courtyard",
    connection: "Stiklių g. through the historic printing court",
    position: { x: 570.558, z: 983.58 },
    radiusM: 17,
  },
  {
    id: "love-arch",
    label: "Love Arch passage",
    connection: "A covered cut-through at Vokiečių g. 22",
    position: { x: 344.398, z: 990.866 },
    radiusM: 16,
  },
  {
    id: "arts-printing-house",
    label: "Arts Printing House courtyard",
    connection: "Šiltadaržio g. into a working arts courtyard",
    position: { x: 930.783, z: 442.823 },
    radiusM: 20,
  },
  {
    id: "courtyard-key-gate",
    label: "Blue courtyard gate",
    connection: "A locked street gate into the residents’ courtyard",
    position: { x: 506.272, z: 384.538 },
    radiusM: 15,
  },
  {
    id: "courtyard-key-roof-route",
    label: "Inner courtyard roof stair",
    connection: "A gated climb from the inner courtyard to the roof",
    position: { x: 491, z: 400.5 },
    radiusM: 15,
  },
  {
    id: "bekesas-hill-steps",
    label: "Bekešas hill steps",
    connection: "The short cobbled climb toward the hilltop path",
    position: { x: 1321.24, z: 234.201 },
    radiusM: 20,
  },
]);

export const TRAVERSAL_GATES: readonly Readonly<TraversalGate>[] =
  Object.freeze([
    {
      id: "courtyard-blue-gate",
      label: "Open the blue courtyard gate",
      position: { x: 506.272, y: 16.604, z: 384.538 },
      widthM: 2.72,
      heightM: 3.1,
      depthM: 0.18,
      rotationYRadians: -0.3642,
      access: "blueGate",
      lockedMessage: "The blue gate is locked. Rūta has the courtyard key.",
    },
    {
      id: "courtyard-roof-gate",
      label: "Open the roof-stair gate",
      position: { x: 508.48, y: 16.504, z: 384.55 },
      widthM: 1.2,
      heightM: 2.9,
      depthM: 0.16,
      rotationYRadians: -0.4755,
      access: "rooftopShortcut",
      lockedMessage: "The stair gate is padlocked. Mantas knows who keeps its key.",
    },
  ]);

function straightStairColliders(
  id: string,
  from: Readonly<{ x: number; y: number; z: number }>,
  to: Readonly<{ x: number; y: number; z: number }>,
  stepCount: number,
  widthM: number,
): readonly AuthoredStaticCollisionSpec[] {
  const deltaX = to.x - from.x;
  const deltaZ = to.z - from.z;
  const horizontalLengthM = Math.hypot(deltaX, deltaZ);
  const direction = { x: deltaX / horizontalLengthM, z: deltaZ / horizontalLengthM };
  const treadDepthM = horizontalLengthM / stepCount;
  const riseM = (to.y - from.y) / stepCount;
  const yaw = Math.atan2(direction.x, direction.z);
  return Array.from({ length: stepCount }, (_, index) => {
    const alpha = (index + 0.5) / stepCount;
    const topY = from.y + riseM * (index + 1);
    const bottomY = Math.min(from.y - 0.12, topY - Math.max(0.14, Math.abs(riseM)));
    return {
      id: `${id}:step:${index + 1}`,
      position: {
        x: from.x + deltaX * alpha,
        y: (topY + bottomY) / 2,
        z: from.z + deltaZ * alpha,
      },
      halfExtentsM: {
        x: widthM / 2,
        y: (topY - bottomY) / 2,
        z: treadDepthM / 2 + 0.015,
      },
      rotationYRadians: yaw,
      classification: "building" as const,
      sourceFeatureId: id,
      enabled: true,
    };
  });
}

function segmentedStairColliders(
  id: string,
  path: readonly Readonly<{ x: number; y: number; z: number }>[],
  stepCount: number,
  widthM: number,
): readonly AuthoredStaticCollisionSpec[] {
  const segments = path.slice(1).map((to, index) => {
    const from = path[index]!;
    return { from, to, lengthM: Math.hypot(to.x - from.x, to.z - from.z) };
  });
  const totalLengthM = segments.reduce((total, segment) => total + segment.lengthM, 0);
  const result: AuthoredStaticCollisionSpec[] = [];
  let assigned = 0;
  for (const [index, segment] of segments.entries()) {
    const count = index === segments.length - 1
      ? stepCount - assigned
      : Math.max(1, Math.round(stepCount * segment.lengthM / totalLengthM));
    const segmentColliders = straightStairColliders(
      `${id}:segment:${index + 1}`,
      segment.from,
      segment.to,
      count,
      widthM,
    );
    result.push(...segmentColliders);
    assigned += count;
  }
  return result.map((spec, index) => ({
    ...spec,
    id: `${id}:step:${index + 1}`,
    sourceFeatureId: id,
  }));
}

const BEKESAS_STAIR_COLLIDERS = segmentedStairColliders(
  "bekesas-hill-steps",
  [
    { x: 1317.421, y: 62.07, z: 244.212 },
    { x: 1318.888, y: 63.073, z: 238.106 },
    { x: 1322.075, y: 64.124, z: 232.203 },
    { x: 1327.925, y: 65.714, z: 224.19 },
  ],
  19,
  1.75,
);

const INNER_ROOF_STAIR_COLLIDERS = straightStairColliders(
  "courtyard-roof-stair-inner",
  { x: 491, y: 15.054, z: 400.5 },
  { x: 504.5, y: 27.654, z: 418.2 },
  48,
  1.15,
).map((spec) => ({ ...spec, enabled: false }));

export const ROOFTOP_SHORTCUT_COLLIDER_IDS: readonly string[] = Object.freeze([
  ...INNER_ROOF_STAIR_COLLIDERS.map((spec) => spec.id),
]);

export const TRAVERSAL_STATIC_COLLIDERS: readonly Readonly<
  AuthoredStaticCollisionSpec
>[] = Object.freeze([
  ...BEKESAS_STAIR_COLLIDERS,
  ...INNER_ROOF_STAIR_COLLIDERS,
  ...TRAVERSAL_GATES.map((gate) => ({
    id: gate.id,
    position: gate.position,
    halfExtentsM: {
      x: gate.widthM / 2,
      y: gate.heightM / 2,
      z: gate.depthM / 2,
    },
    rotationYRadians: gate.rotationYRadians,
    classification: "building" as const,
    sourceFeatureId: gate.id,
    enabled: true,
  })),
]);

export const TRAVERSAL_INTERACTION_RADIUS_M = 2.6;
export const TRAVERSAL_DISCOVERY_VISIBLE_SECONDS = 5.5;
